<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="th-question">
        <span class="th-bar th-bar--xl"></span>
      </div>
      <div class="th-queue">
        <div
          v-for="(q, i) in queue"
          :key="q.text"
          class="th-row"
          :class="{ '--now': i === 0 }"
        >
          <div class="th-slot">
            <span class="material-symbols-rounded th-slot-icon">schedule</span>
            <span class="th-slot-time">{{ q.time }}</span>
          </div>
          <div class="th-row-content">
            <span class="th-row-text">{{ q.text }}</span>
            <span class="th-row-meta">{{ q.author }} · {{ q.votes }} votes</span>
          </div>
          <span v-if="i === 0" class="th-now-badge">Now</span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="th-phone-q"></span>
      <div class="th-phone-input">
        <span class="th-phone-typed">When does the rollout start?<span class="th-caret">|</span></span>
      </div>
      <div class="th-phone-budget">
        <span class="th-phone-budget-label">Time left</span>
        <div class="th-phone-budget-track">
          <div class="th-phone-budget-fill"></div>
        </div>
        <span class="th-phone-budget-time">2:14</span>
      </div>
      <button type="button" class="th-phone-submit" tabindex="-1">
        <span class="material-symbols-rounded">send</span>
        Submit
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewTownHall',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      queue: [
        {
          time: '2:00', text: 'When does rollout start?', author: 'Ravi', votes: 32,
        },
        {
          time: '3:00', text: 'Hiring plans for Q3?', author: 'Anonymous', votes: 24,
        },
        {
          time: '2:00', text: 'Will support hours change?', author: 'Lin', votes: 14,
        },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.th-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.th-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.th-queue { display: grid; gap: 6px; }
.th-row {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  background: rgba(15, 15, 20, 0.03);
  border: 1px solid rgba(15, 15, 20, 0.06);
}
.th-row.--now {
  background: color-mix(in srgb, var(--accent) 10%, white);
  border-color: color-mix(in srgb, var(--accent) 35%, transparent);
}
.th-slot {
  display: flex; align-items: center; gap: 2px;
  font-size: 9px; font-weight: 700;
  color: rgba(15, 15, 20, 0.5);
}
.th-row.--now .th-slot { color: var(--accent); }
.th-slot-icon { font-size: 12px; }
.th-slot-time { font-variant-numeric: tabular-nums; }

.th-row-content { display: grid; gap: 2px; min-width: 0; }
.th-row-text {
  font-size: 11px; font-weight: 700;
  color: rgba(15, 15, 20, 0.78);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.th-row-meta {
  font-size: 9px; font-weight: 600;
  color: rgba(15, 15, 20, 0.45);
}
.th-now-badge {
  background: var(--accent); color: white;
  font-size: 9px; font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 6px;
  border-radius: 999px;
  animation: th-pulse 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

/* ---- Audience phone ---- */
.th-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.th-phone-input {
  border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
  border-radius: 8px;
  padding: 9px 9px;
  background: white;
}
.th-phone-typed {
  font-size: 11px; font-weight: 600;
  color: rgba(15, 15, 20, 0.8);
}
.th-caret { color: var(--accent); animation: th-blink 1s steps(2) infinite; }

.th-phone-budget {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  background: color-mix(in srgb, var(--accent) 8%, white);
  border-radius: 6px;
}
.th-phone-budget-label {
  font-size: 9px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  text-transform: uppercase; letter-spacing: 0.04em;
}
.th-phone-budget-track {
  height: 6px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.08);
  overflow: hidden;
}
.th-phone-budget-fill {
  height: 100%; width: 100%;
  background: var(--accent);
  animation: th-drain 4s linear infinite;
}
.th-phone-budget-time {
  font-size: 10px; font-weight: 800;
  color: var(--accent);
  font-variant-numeric: tabular-nums;
}

.th-phone-submit {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  cursor: pointer;
  animation: th-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.th-phone-submit .material-symbols-rounded { font-size: 14px; }

@keyframes th-pulse {
  0%, 25%   { transform: scale(1); }
  35%       { transform: scale(1.15); }
  50%, 100% { transform: scale(1); }
}
@keyframes th-drain {
  0%   { width: 100%; }
  100% { width: 35%; }
}
@keyframes th-blink { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes th-tap {
  0%, 60%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  72%       { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%      { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .th-now-badge, .th-phone-budget-fill, .th-caret, .th-phone-submit { animation: none; }
  .th-phone-budget-fill { width: 60%; }
}
</style>
